import { uuid } from './uuid'
import { openDB } from 'idb'
import { draftCount, storageError, draftRevision, queuedCount, reviewCount } from './runtime'
import { reportPwaError } from './diagnostics'
const DB = 'smartfarm-drafts'
let connection
const db = () => connection ||= openDB(DB, 3, { upgrade(db) {
  if (!db.objectStoreNames.contains('drafts')) db.createObjectStore('drafts', { keyPath: 'id' })
  if (!db.objectStoreNames.contains('walks')) db.createObjectStore('walks', { keyPath: 'owner' })
  if (!db.objectStoreNames.contains('reads')) db.createObjectStore('reads', { keyPath: 'key' })
}, blocking() { connection?.then(d => d.close()); connection = null } })
export function ownerKey() {
  try { const u = JSON.parse(localStorage.getItem('user')); return u?.id && u?.farm_id ? `${u.farm_id}:${u.id}` : null } catch { return null }
}
export async function listDrafts(owner = ownerKey()) {
  if (!owner) return []
  return (await (await db()).getAll('drafts')).filter(d => d.owner === owner).sort((a,b) => b.updatedAt.localeCompare(a.updatedAt))
}
export async function refreshDraftCount() {
  try {
    const rows = await listDrafts()
    draftCount.value = rows.filter(d => d.status !== 'sent').length
    queuedCount.value = rows.filter(d => ['queued','uncertain','sending'].includes(d.status)).length
    reviewCount.value = rows.filter(d => d.status === 'review').length
  } catch { storageError.value = 'Không truy cập được bộ nhớ bản nháp.' }
}
export async function getDraft(id) {
  const d = await (await db()).get('drafts', id)
  return d?.owner === ownerKey() ? d : null
}
export async function putDraft(input) {
  const owner = input.owner || ownerKey()
  if (!owner || owner !== ownerKey()) throw Error('Cần đăng nhập lại để lưu bản nháp.')
  try {
    // Vue's reactive objects cannot be structured-cloned by IndexedDB. Keep
    // the photo as a Blob while normalizing the JSON business snapshot.
    input = { ...JSON.parse(JSON.stringify({ ...input, file: undefined })), file: input.file || null }
    const database = await db()
    const tx = database.transaction('drafts', 'readwrite')
    tx.done.catch(() => {})
    const rows = await tx.store.getAll()
    const old = rows.find(d => d.id === input.id)
    if (old && old.owner !== owner) { tx.abort(); throw Error('Bản nháp thuộc tài khoản khác.') }
    if (old && ['uncertain','sending','sent','queued','review'].includes(old.status) && !input.status) { await tx.done; return old }
    if (input.file?.size > 5*1024*1024) { tx.abort(); throw Error('Ảnh tối đa 5 MB.') }
    const total = rows.filter(d => d.id !== input.id).reduce((n,d) => n+(d.file?.size || 0),0)+(input.file?.size || 0)
    if (total > 30*1024*1024) { tx.abort(); throw Error('Ảnh nháp đã vượt 30 MB. Vui lòng gửi hoặc xóa nháp cũ.') }
    const [farmId, userId] = owner.split(':')
    const row = { schema: 1, schemaVersion: 1, status: 'draft', key: uuid(), ...old, ...input, owner, farmId, userId, updatedAt: new Date().toISOString() }
    await tx.store.put(row); await tx.done
    requestPersistentStorage()
    storageError.value = ''; draftRevision.value++; await refreshDraftCount()
    return row
  } catch(e) { reportPwaError('SF_DRAFT_SAVE_FAILED'); if (e?.name === 'QuotaExceededError') e = Error('Bộ nhớ thiết bị đã đầy, chưa lưu được bản nháp.')
    storageError.value = e.message || 'Không lưu được bản nháp.'; throw e }
}
export async function deleteDraft(id) {
  if (!await getDraft(id)) return
  await (await db()).delete('drafts', id); draftRevision.value++; await refreshDraftCount()
}
export async function clearDrafts(owner = ownerKey()) {
  const database = await db(); const tx = database.transaction(['drafts','walks'], 'readwrite')
    tx.done.catch(() => {})
  for (const d of await tx.objectStore('drafts').getAll()) if (d.owner === owner) await tx.objectStore('drafts').delete(d.id)
  await tx.objectStore('walks').delete(owner)
  await tx.done; draftRevision.value++; await refreshDraftCount()
  await clearReads(owner)
}

// ---- Offline read cache (F13.3): last good answer of the few GETs the
// offline-capable screens need, per account. Never throws.
export async function putRead(key, data) {
  const owner = ownerKey()
  if (!owner) return
  try { await (await db()).put('reads', { key: `${owner}|${key}`, owner, data, at: new Date().toISOString() }) } catch { /* best effort */ }
}
export async function getRead(key) {
  const owner = ownerKey()
  if (!owner) return null
  try { return (await (await db()).get('reads', `${owner}|${key}`)) || null } catch { return null }
}
async function clearReads(owner) {
  try {
    const tx = (await db()).transaction('reads', 'readwrite')
    for (const r of await tx.store.getAll()) if (r.owner === owner) await tx.store.delete(r.key)
    await tx.done
  } catch { /* best effort */ }
}
export async function saveWalk(value) {
  const owner = ownerKey()
  if (!owner) throw Error('Cần đăng nhập lại để giữ vị trí lượt đi.')
  await (await db()).put('walks', { ...value, owner })
}
export async function getWalk() {
  const owner = ownerKey()
  return owner ? (await db()).get('walks', owner) : null
}


// ---- Public API for business screens (see src/pwa/README.md) ----
export const DRAFT_STATUS_LABEL = { draft: 'Nháp trên máy', queued: 'Chờ đồng bộ', sending: 'Đang gửi', uncertain: 'Chưa xác định kết quả · sẽ tự gửi lại', review: 'Cần xem lại', sent: 'Đã gửi' }
export const draftStatusLabel = status => DRAFT_STATUS_LABEL[status] || DRAFT_STATUS_LABEL.draft
export const draftId = (kind, key) => `${kind}:${key}`
export async function countPending(owner = ownerKey()) {
  return (await listDrafts(owner)).filter(d => d.status !== 'sent').length
}
async function setStatus(id, status, extra = {}) {
  const d = await getDraft(id)
  if (!d) throw Error('Không tìm thấy bản nháp của tài khoản này.')
  return putDraft({ ...d, status, ...extra })
}
export const markSubmitting = id => setStatus(id, 'sending', { error: '' })
export const markUncertain = (id, error = '') => setStatus(id, 'uncertain', { error })
export const markFailed = (id, error = '') => setStatus(id, 'draft', { error })
export const markSent = (id, result) => setStatus(id, 'sent', { result, file: null, error: '' })
// Ask the browser not to evict drafts under storage pressure. Never throws.
export async function requestPersistentStorage() {
  try { if (navigator.storage?.persist && !(await navigator.storage.persisted())) await navigator.storage.persist() } catch { /* best effort */ }
}
